import { useQuery } from '@tanstack/react-query'
import { hasLiveApi } from '@/api/client'
import {
  demoCommissionEligibility,
  fetchCommissionEligibility,
  fetchCommissionEventBets,
  unavailableCommissionEligibility,
  type CommissionEligibilityQuery,
  type CommissionEligibilityResult,
} from '@/api/map/commissionEligibility'
import { keys } from '@/api/hooks/shared/keys'

async function load(query: CommissionEligibilityQuery): Promise<CommissionEligibilityResult> {
  if (!query.from || !query.to) {
    return unavailableCommissionEligibility(query, 'Choose a week to search.')
  }
  if (!hasLiveApi()) {
    return demoCommissionEligibility(query)
  }
  try {
    return await fetchCommissionEligibility(query)
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    return unavailableCommissionEligibility(query, message || 'Commission eligibility failed.')
  }
}

export function useCommissionEligibility(query: CommissionEligibilityQuery, enabled = true) {
  return useQuery({
    queryKey: keys.commissionEligibility(query),
    queryFn: () => load(query),
    enabled: enabled && Boolean(query.scopeId && query.from && query.to),
    placeholderData: (prev) => prev,
  })
}

/** Lab events drawer — Agent-API `bqGetEventWiseBetCount`. */
export function useCommissionEventBets(
  args: {
    from: string
    to: string
    playerId: string
    isEligible: boolean
  },
  enabled = true,
) {
  return useQuery({
    queryKey: ['commission-event-bets', args] as const,
    queryFn: async () => {
      try {
        return await fetchCommissionEventBets({
          from: args.from,
          to: args.to,
          userIds: [args.playerId],
          isEligible: args.isEligible,
        })
      } catch {
        return []
      }
    },
    enabled: enabled && Boolean(args.from && args.to && args.playerId) && hasLiveApi(),
  })
}
